import {useEffect, useState, useRef} from "react";
import {
  Phone,
  User,
  Mail,
  MapPin,
  Tag,
  Search,
  PhoneOutgoing,
  Scroll,
} from "lucide-react";
import {FaPlay, FaPause} from "react-icons/fa";
import moment from "moment-timezone";

/* UTILITY COMPONENTS */
/**
 * Renders a single detail item (label and value).
 */
const DetailItem = ({icon: Icon, label, value}) => (
  <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg shadow-sm">
    <Icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#2ba9cd]" />
    <div className="flex flex-col">
      <span className="text-xs font-semibold uppercase text-gray-500">
        {label}
      </span>
      <span className="text-sm font-medium text-gray-800 break-words">
        {value || "--"}
      </span>
    </div>
  </div>
);

const ViewDetails = ({
  selectedCallback,
  setIsModalOpen,
  setIsTranscriptModalOpen,
}) => {
  return (
    <div className="flex flex-col bg-white h-[75vh] overflow-y-auto p-4">
      <div className="flex justify-between items-start border-b pb-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-1">
            {selectedCallback.customer_firstname}{" "}
            {selectedCallback.customer_lastname}
          </h1>
          <p className="flex items-center text-lg text-[#2ba9cd] font-mono">
            {selectedCallback.customer_phonenumber}
            <PhoneOutgoing
              className="w-5 h-5 ml-2 cursor-pointer text-red-500"
              onClick={() => setIsModalOpen(true)}
            />
          </p>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <DetailItem
          icon={User}
          label="First Name"
          value={selectedCallback.customer_firstname}
        />
        <DetailItem
          icon={User}
          label="Last Name"
          value={selectedCallback.customer_lastname}
        />
        <DetailItem
          icon={Mail}
          label="Email"
          value={selectedCallback.customer_email}
        />
        <DetailItem
          icon={Tag}
          label="Program"
          value={selectedCallback.client_name?.toLowerCase() || "--"}
        />
      </div>

      {/* Address Section */}
      <h3 className="text-xl font-semibold text-gray-700 mt-4 mb-3 border-b pb-1">
        Location
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <DetailItem
          icon={MapPin}
          label="Address"
          value={selectedCallback.customer_address}
        />
        <DetailItem
          icon={MapPin}
          label="City"
          value={selectedCallback.customer_city}
        />
        <DetailItem
          icon={MapPin}
          label="State"
          value={selectedCallback.customer_state}
        />
        <DetailItem
          icon={MapPin}
          label="Country"
          value={selectedCallback.customer_country}
        />
      </div>

      {/* ContactId Section */}
      <h3 className="text-xl font-semibold text-gray-700 mt-4 mb-3 border-b pb-1">
        Contact Id
      </h3>
      <div className="p-4 bg-[#E7F7FB] rounded-lg border border-[#2ba9cd]">
        <div className="flex items-center justify-start text-sm text-gray-700">
          <Phone className="w-4 h-4 mr-2 text-[#2ba9cd] flex-shrink-0" />
          <p>{selectedCallback.ContactId}</p>
        </div>
      </div>

      {/* Recording Section */}
      <h3 className="text-xl font-semibold text-gray-700 mt-4 mb-3 border-b pb-1">
        Recording and Transcript
      </h3>
      <div className="flex p-2 items-center">
        <RecordingPlayer recordingUrl={selectedCallback.combinedAudio} />
        <Scroll
          className="w-7 h-7 ml-4 flex-shrink-0 cursor-pointer text-[#2ba9cd]"
          onClick={() => setIsTranscriptModalOpen(true)}
        />
      </div>
    </div>
  );
};

const RecordingPlayer = ({recordingUrl}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    if (recordingUrl && !audioRef.current) {
      const audio = new Audio(recordingUrl);
      audioRef.current = audio;

      const handleTimeUpdate = () => {
        if (audio.duration && isFinite(audio.duration)) {
          setProgress((audio.currentTime / audio.duration) * 100);
        }
      };

      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setProgress(100);
      };

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);

      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("play", handlePlay);
      audio.addEventListener("pause", handlePause);

      return () => {
        audio.pause();
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("ended", handleEnded);
        audio.removeEventListener("play", handlePlay);
        audio.removeEventListener("pause", handlePause);
        audioRef.current = null;
      };
    }
    return () => {};
  }, [recordingUrl]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      try {
        if (audio.ended || progress >= 100) {
          audio.currentTime = 0;
          setProgress(0);
        }
        await audio.play();
      } catch (error) {
        console.error("Error attempting to play audio:", error.message);
      }
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const newTime = (e.target.value / 100) * audio.duration;
    audio.currentTime = newTime;
    setProgress(e.target.value);
  };

  const currentTime = audioRef.current
    ? Math.floor(audioRef.current.currentTime)
    : 0;

  const displayDuration =
    duration && isFinite(duration) ? Math.floor(duration) : 30;

  const formattedTime = `${Math.floor(currentTime / 60)}:${(currentTime % 60)
    .toString()
    .padStart(2, "0")}`;
  const formattedDuration = `${Math.floor(displayDuration / 60)}:${(
    displayDuration % 60
  )
    .toString()
    .padStart(2, "0")}`;

  if (!recordingUrl) {
    return <span className="text-gray-400 text-xs italic">No Recording</span>;
  }

  return (
    <div className="flex items-center space-x-2 w-full max-w-xs bg-gray-50 p-2 rounded-lg border border-gray-200">
      <button
        onClick={togglePlay}
        className={`p-1 text-[#2ba9cd]`}
        title={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? <FaPause /> : <FaPlay />}
      </button>

      <div className="flex-1 flex items-center">
        <input
          type="range"
          min="0"
          max="100"
          value={progress}
          onChange={handleSeek}
          className="w-full h-2 bg-gray-300 rounded-full appearance-none cursor-pointer accent-[#2ba9cd]"
          style={{
            background: `linear-gradient(to right, #2ba9cd ${progress}%, #d1d5db ${progress}%)`,
          }}
        />
      </div>

      <span className="text-xs text-gray-600 font-mono text-right whitespace-nowrap">
        {formattedTime} / {formattedDuration}
      </span>
    </div>
  );
};

const MessageBubble = ({transcript}) => {
  return transcript.map((msg, index) => (
    <div
      className={
        msg.Author === "caller"
          ? "p-2 bg-[#c8f3ff] rounded-lg w-[70%] ml-auto border border-[#2ba9cd]"
          : "p-2 bg-gray-100 rounded-lg w-[70%] mr-auto border border-gray-400"
      }
      key={index}
    >
      <div className="flex justify-between mb-1">
        <p
          className={
            msg.Author === "caller"
              ? `text-xs font-semibold text-[#2ba9cd]`
              : `text-xs font-semibold text-gray-700`
          }
        >
          {msg.Author === "caller" ? "Customer" : "Agent"}
        </p>
      </div>
      <p
        className={
          msg.Author === "caller"
            ? `text-base text-[#2ba9cd]`
            : `text-base text-gray-500`
        }
      >
        {msg.Text}
      </p>
    </div>
  ));
};

const OrbitLoader = () => (
  <div className="relative h-10 w-10 animate-spin">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#2ba9cd] rounded-full"></div>
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-[#2ba9cd]/50 rounded-full"></div>
  </div>
);

// 1. Left Navigation Pane (Phone List)
const CallbackList = ({
  filteredCallbacks,
  searchTerm,
  selectedCallback,
  setSearchTerm,
  setSelectedCallback,
}) => (
  <div className="flex flex-col bg-white border-r border-gray-200 overflow-y-auto">
    <div className="p-4 border-b">
      <h2 className="text-xl font-bold text-gray-800 flex items-center">
        <Phone className="w-5 h-5 mr-2 text-[#2ba9cd]" />
        Callback Directory
      </h2>

      <div className="relative mt-2 mb-3">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name or phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-2 pl-10 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
        />
      </div>
    </div>
    <div className="h-[60vh] overflow-y-auto overflow-x-auto">
      {filteredCallbacks?.length > 0 ? (
        filteredCallbacks.map((callback) => (
          <div
            key={callback.ContactId}
            className={`px-3 py-2 space-y-1 cursor-pointer transition duration-150 ease-in-out border-b last:border-b-0 
                                ${
                                  selectedCallback &&
                                  selectedCallback.ContactId ===
                                    callback.ContactId
                                    ? "bg-[#E7F7FB] border-[#2ba9cd] border-l-4"
                                    : "hover:bg-gray-50 border-l-4 border-[#E7F7FB]"
                                }`}
            onClick={() => {
              setSelectedCallback(callback);
            }}
          >
            <p className="text-sm text-gray-900">
              {!callback.start_time
                ? "--"
                : `${moment
                    .tz(callback.start_time, "America/Los_Angeles")
                    .format("MM/DD/YYYY hh:mm A")}`}
            </p>
            <p className="text-xs text-gray-400">
              {callback.customer_phonenumber}
            </p>
          </div>
        ))
      ) : (
        <p className="p-4 text-center text-gray-500 text-sm italic">
          {searchTerm
            ? `No missed interactions match "${searchTerm}".`
            : "No missed interactions found."}
        </p>
      )}
    </div>
  </div>
);

// 2. Right Content Pane (Details or Form)
const CallbackPane = ({
  selectedCallback,
  setIsModalOpen,
  setIsTranscriptModalOpen,
}) => {
  if (selectedCallback) {
    return (
      <ViewDetails
        selectedCallback={selectedCallback}
        setIsModalOpen={setIsModalOpen}
        setIsTranscriptModalOpen={setIsTranscriptModalOpen}
      />
    );
  }

  return (
    <div className="flex h-full items-center justify-center text-gray-500">
      <div className="text-center">
        <p className="text-lg">Select a callback from the list.</p>
      </div>
    </div>
  );
};

const Callbacks = (props) => {
  const [callbacks, setCallbacks] = useState([]);
  const [selectedCallback, setSelectedCallback] = useState();
  const [searchTerm, setSearchTerm] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTranscriptModalOpen, setIsTranscriptModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState("");

  const [queueOptions, setQueueOptions] = useState([
    {
      value: "",
      name: "--Please Select--",
    },
  ]);

  const [isLoading, setIsLoading] = useState(false);

  const filteredCallbacks = callbacks.filter((callback) => {
    if (!searchTerm) return true;

    const lowerCaseSearch = searchTerm.toLowerCase();

    return (
      callback.customer_phonenumber.toLowerCase().includes(lowerCaseSearch) ||
      callback.customer_firstname.toLowerCase().includes(lowerCaseSearch) ||
      callback.customer_lastname.toLowerCase().includes(lowerCaseSearch)
    );
  });

  useEffect(() => {
    let queues = props.agentQueues?.filter((x) => {
      return x && x !== null;
    });
    if (queues && queues.length > 0) {
      setQueueOptions([
        {
          value: "",
          name: "--Please Select--",
        },
        ...queues,
      ]);
    }
  }, [props.agentQueues]);

  useEffect(() => {
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: process.env.REACT_APP_AUTH_TOKEN,
      },
    };

    setIsLoading(true);
    fetch(`${process.env.REACT_APP_AWS_ENDPOINT}/get-callbacks`, options)
      .then((resp) => resp.json())
      .then((data) => {
        let assignedCallbacks = data;
        let queues = [];
        if (props.agentQueues) {
          queues = props.agentQueues?.map((x) => {
            if (x && x?.name !== "null") {
              return x.name.toLowerCase();
            } else return null;
          });

          assignedCallbacks = data.filter((x) =>
            queues?.includes(x.client_name),
          );
        }

        assignedCallbacks.sort(function (a, b) {
          return new Date(b.start_time) - new Date(a.start_time);
        });
        setCallbacks(assignedCallbacks);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(`Error in getting recordings =======> ${error}`);
        setIsLoading(false);
      });
  }, [props.agentQueues]);

  useEffect(() => {
    const isSelectedCallbacksFilteredOut =
      selectedCallback &&
      filteredCallbacks.every(
        (c) => c.ContactId !== selectedCallback.ContactId,
      );

    if (callbacks.length > 0 && !selectedCallback) {
      if (filteredCallbacks.length > 0) {
        setSelectedCallback(filteredCallbacks[0]);
      }
    } else if (isSelectedCallbacksFilteredOut && filteredCallbacks.length > 0) {
      setSelectedCallback(filteredCallbacks[0]);
    } else if (callbacks.length === 0) {
      setSelectedCallback(null);
    }
  }, [callbacks, filteredCallbacks]);

  const callbackListProps = {
    filteredCallbacks,
    searchTerm,
    selectedCallback,
    setSearchTerm,
    setSelectedCallback,
  };

  const callbackPaneProps = {
    selectedCallback,
    setIsModalOpen,
    setIsTranscriptModalOpen,
  };

  const handleSubmit = () => {
    setIsModalOpen(false);
    props.makeDirectOutboundCall(
      selectedCallback.customer_phonenumber,
      selectedQueue,
    );
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const closeTranscriptModal = () => {
    setIsTranscriptModalOpen(false);
  };

  return (
    <div className="flex flex-col p-4 space-y-6">
      {isLoading === false ? (
        <div className="flex justify-between bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="flex w-full">
            <div className="w-1/3">
              <CallbackList {...callbackListProps} />
            </div>

            <div className="w-2/3 bg-white">
              <CallbackPane {...callbackPaneProps} />
            </div>
          </div>

          {isModalOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
              onClick={closeModal}
            >
              <div
                className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl transform transition-all duration-300 scale-100 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-2xl font-semibold text-gray-800">
                    Outbound Call
                  </h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  <div className="flex flex-col space-y-1">
                    <label
                      htmlFor="queue"
                      className="text-sm font-medium text-gray-700"
                    >
                      Target Queue
                    </label>
                    <select
                      id="queue"
                      value={selectedQueue}
                      onChange={(e) => setSelectedQueue(e.target.value)}
                      required
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white pr-8"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='currentColor'%3e%3cpath d='M7 7l3-3 3 3m0 6l-3 3-3-3' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 0.75rem center",
                        backgroundSize: "1.5em 1.5em",
                      }}
                    >
                      <option value="" disabled>
                        Select a routing queue...
                      </option>
                      {queueOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex justify-end pt-4 space-x-3">
                    <button
                      onClick={closeModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg disabled:opacity-50"
                      disabled={!selectedQueue}
                      onClick={handleSubmit}
                    >
                      Call
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {isTranscriptModalOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
              onClick={closeTranscriptModal}
            >
              <div
                className="bg-white w-full max-w-2xl h-[80vh] rounded-2xl shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-2xl font-semibold text-gray-800">
                    Call Transcript
                  </h3>
                  <button
                    onClick={closeTranscriptModal}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                </div>
                <div className="flex flex-col space-y-4 h-[62vh] overflow-y-auto p-4">
                  <MessageBubble transcript={selectedCallback.transcription} />
                </div>
                <div className="flex justify-end p-4 space-x-3">
                  <button
                    onClick={closeTranscriptModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex w-full h-[70vh] justify-center items-center">
          <OrbitLoader />
        </div>
      )}
    </div>
  );
};

export default Callbacks;
